import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Grid,
  HStack,
  Heading,
  IconButton,
  ListItem,
  Spinner,
  Stack,
  Text,
  Tooltip,
  UnorderedList,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { CheckIcon, CopyIcon } from "@chakra-ui/icons";
import { DangerButton } from "@sciencecorp/helix-components";
import {
  useAdminAccount,
  useDeleteAccountApplication,
  type AdminAccountDetail,
} from "@/api/admin/accounts";
import { formatDate } from "@/utils/dateFormat";
import { localeLabels, type SupportedLocale } from "@/i18n";
import { ConsentGatedField } from "./ConsentGatedField";
import {
  formatStatus,
  getCountryName,
  getSubdivisionName,
  formatBoolean,
  lifecycleOf,
} from "./utils";

// Renders the locale code as its English-language label (e.g. "en" → "English").
const formatLocale = (locale: string): string =>
  localeLabels[locale as SupportedLocale] ?? locale;

const useCopy = () => {
  const [copiedKey, setCopiedKey] = React.useState<string | null>(null);
  const copy = async (key: string, value: string | null) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    } catch {
      /* clipboard failed (insecure context) — no-op */
    }
  };
  return { copiedKey, copy };
};

interface CopyableProps {
  label: string;
  value: string | null;
  copyKey: string;
  copiedKey: string | null;
  onCopy: (key: string, value: string | null) => void;
}

const CopyableField = ({ label, value, copyKey, copiedKey, onCopy }: CopyableProps) => (
  <Flex alignItems="center" gap={3}>
    <Box minWidth="160px">
      <Text fontWeight="medium">{label}</Text>
    </Box>
    <Flex alignItems="center" gap={2} flex="1" minWidth={0}>
      <Text isTruncated>{value ?? "—"}</Text>
      <Tooltip label={copiedKey === copyKey ? "Copied" : "Copy"} placement="top" hasArrow>
        <IconButton
          aria-label={`Copy ${label}`}
          icon={copiedKey === copyKey ? <CheckIcon /> : <CopyIcon />}
          size="sm"
          variant="ghost"
          onClick={() => onCopy(copyKey, value)}
          isDisabled={!value}
        />
      </Tooltip>
    </Flex>
  </Flex>
);

const SimpleField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <HStack alignItems="flex-start">
    <Text fontWeight="medium" width="160px">
      {label}
    </Text>
    <Text>{value}</Text>
  </HStack>
);

// Personal data lives on the application's patient_*/proxy_* fields, not on
// the User columns (which the signup flow never populates). Pick the right
// persona based on applicant_type. Falls back to User.first_name/last_name
// when there's no application or the persona block is empty.
const getPersonalSource = (account: AdminAccountDetail) => {
  if (account.applicant_type === "self") return account.patient;
  if (account.applicant_type === "proxy") return account.proxy;
  return null;
};

const dashIfBlank = (v: string | null | undefined): string => v?.trim() || "—";

// PersonaSection only ever renders a Patient (the proxy persona is shown
// inline in the Personal section, not as its own card), so the prop is
// narrowed to Patient — no `"age_of_majority" in persona` cast.
interface PersonaSectionProps {
  title: string;
  patient: AdminAccountDetail["patient"];
}

const PersonaSection = ({ title, patient }: PersonaSectionProps) => {
  const fullName =
    [patient.first_name, patient.last_name].filter(Boolean).join(" ") || "—";
  return (
    <Box>
      <Heading size="md" marginBottom={4}>
        {title}
      </Heading>
      <Stack spacing={3}>
        <SimpleField label="Name" value={fullName} />
        <SimpleField label="Phone" value={patient.phone ?? "—"} />
        <SimpleField label="Country" value={getCountryName(patient.country) || "—"} />
        <SimpleField label="State / region" value={getSubdivisionName(patient.state)} />
        <SimpleField
          label="Date of birth"
          value={patient.date_of_birth ? formatDate(patient.date_of_birth) : "—"}
        />
        <SimpleField label="Adult" value={formatBoolean(patient.age_of_majority)} />
      </Stack>
    </Box>
  );
};

export const AccountDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: account, isLoading, error } = useAdminAccount(id);
  const { copiedKey, copy } = useCopy();
  const deleteApplication = useDeleteAccountApplication();
  const toast = useToast();

  // DangerButton awaits onProceed and only clears its spinner / closes the
  // modal once it resolves, so this must never reject — surface failures as a
  // toast instead.
  const handleDeleteApplication = async (accountId: string, email: string) => {
    try {
      await deleteApplication.mutateAsync(accountId);
      toast({
        title: "Application deleted",
        description: `${email} is back to a post-signup state and can start a new application.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to delete application",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Back-to-list: if there's an in-app entry use it (so the filtered URL is
  // restored). Otherwise navigate to the clean /admin/accounts.
  const cameFromInApp = location.key !== "default";
  const handleBack = () => (cameFromInApp ? navigate(-1) : navigate("/admin/accounts"));

  if (isLoading) {
    return (
      <Center py={20}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Box padding={8}>
        <VStack spacing={4}>
          <Heading>Error Loading Account</Heading>
          <Text color="red.500">
            {error instanceof Error ? error.message : "Unknown error occurred"}
          </Text>
          <Button onClick={handleBack} colorScheme="blue">
            Back to Accounts
          </Button>
        </VStack>
      </Box>
    );
  }

  if (!account) {
    return (
      <Box padding={8}>
        <VStack spacing={4}>
          <Heading>Account Not Found</Heading>
          <Text>No account found for ID: {id}</Text>
          <Button onClick={handleBack} colorScheme="blue">
            Back to Accounts
          </Button>
        </VStack>
      </Box>
    );
  }

  const isProxy = account.applicant_type === "proxy";
  const isSelf = account.applicant_type === "self";
  const lifecycle = lifecycleOf(account);
  const isDraft = lifecycle.kind === "draft";
  // Covers drafts as well as submitted applications — a wrong answer on the
  // first step is exactly the case that leaves someone stuck mid-flow.
  const hasApplication = lifecycle.kind !== "none";

  const personalSource = getPersonalSource(account);
  // Prefer app-sourced names for the page header; fall back to User columns
  // (which are usually empty) and ultimately to the email.
  const displayName =
    [personalSource?.first_name, personalSource?.last_name].filter(Boolean).join(" ") ||
    [account.first_name, account.last_name].filter(Boolean).join(" ") ||
    account.email;

  // The response collapses drafts into "none"; we re-expand them here using
  // application_started_at (via lifecycleOf) so admins see the
  // started-but-not-submitted state surfaced explicitly.
  const statusInfo: { text: string; colorScheme: string } = (() => {
    switch (lifecycle.kind) {
      case "none":
        return { text: "No application", colorScheme: "gray" };
      case "draft":
        return { text: "Application started — not submitted", colorScheme: "orange" };
      default: {
        const s = formatStatus(lifecycle.kind);
        return { text: s.text, colorScheme: s.colorScheme };
      }
    }
  })();

  const bannerColor = isProxy ? "orange" : isSelf ? "blue" : isDraft ? "yellow" : "gray";
  const bannerText = isProxy
    ? `Proxy application — this account is the proxy for ${
        [account.patient.first_name, account.patient.last_name].filter(Boolean).join(" ") ||
        "the patient"
      }`
    : isSelf
      ? "Self application — the account holder is the patient"
      : isDraft
        ? "Application started — applicant type not yet set"
        : null;

  return (
    <Box padding={[4, 6, 8]} maxWidth="container.xl" margin="0 auto">
      <VStack align="stretch" spacing={2} marginBottom={4}>
        <Button
          variant="link"
          size="sm"
          alignSelf="flex-start"
          onClick={handleBack}
          colorScheme="blue"
        >
          ← Accounts
        </Button>
        <Heading size="lg">{displayName}</Heading>
        <Text color="gray.600">{account.email}</Text>
      </VStack>

      {bannerText && (
        <Box
          marginTop={4}
          padding={3}
          borderRadius="md"
          backgroundColor={`${bannerColor}.50`}
          borderLeft="4px solid"
          borderColor={`${bannerColor}.400`}
        >
          <Text fontWeight="bold" color={`${bannerColor}.800`}>
            {bannerText}
          </Text>
        </Box>
      )}

      <Divider marginTop={6} />

      <Grid gap={8} paddingBlockStart={8} templateColumns={["1fr", "1fr", "1fr", "1fr 1fr"]}>
        <VStack align="stretch" gap={8}>
          {/* Personal: sourced from the application's patient_ / proxy_
              fields based on applicant_type. For self apps: patient_. For
              proxy apps: proxy_ (the proxy IS the account holder). With no
              application, falls back to the User columns (typically empty
              since the signup flow doesn't populate them). */}
          <Box>
            <Heading size="md" marginBottom={4}>
              Personal
            </Heading>
            <Stack spacing={3}>
              <SimpleField
                label="First name"
                value={dashIfBlank(personalSource?.first_name ?? account.first_name)}
              />
              <SimpleField
                label="Last name"
                value={dashIfBlank(personalSource?.last_name ?? account.last_name)}
              />
              <SimpleField
                label="Date of birth"
                value={
                  personalSource?.date_of_birth
                    ? formatDate(personalSource.date_of_birth)
                    : "—"
                }
              />
              <SimpleField
                label="Country"
                value={getCountryName(personalSource?.country ?? null) || "—"}
              />
              <SimpleField
                label="State / region"
                value={getSubdivisionName(personalSource?.state ?? null)}
              />
              {isSelf && (
                <SimpleField
                  label="Adult"
                  value={formatBoolean(account.patient.age_of_majority)}
                />
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Contact: email and phone gated on consent flags. Phone sources
              from the relevant persona on the application; email is the
              account email. */}
          <Box>
            <Heading size="md" marginBottom={4}>
              Contact
            </Heading>
            <Stack spacing={3}>
              <ConsentGatedField
                channel="email"
                value={account.email}
                consent={account.consent}
              />
              <ConsentGatedField
                channel="phone"
                value={personalSource?.phone ?? null}
                consent={account.consent}
              />
              <SimpleField
                label="Recurring contact"
                value={
                  <Text color={account.consent.contact_recurring ? "green.600" : "red.600"}>
                    {account.consent.contact_recurring ? "Granted" : "Declined"}
                  </Text>
                }
              />
              <SimpleField
                label="General contact"
                value={
                  <Text color={account.consent.contact ? "green.600" : "red.600"}>
                    {account.consent.contact ? "Granted" : "Declined"}
                  </Text>
                }
              />
            </Stack>
          </Box>
        </VStack>

        <VStack align="stretch" gap={8}>
          {/* Account: long IDs admins copy into other tools. */}
          <Box>
            <Heading size="md" marginBottom={4}>
              Account
            </Heading>
            <Stack spacing={3}>
              <CopyableField
                label="Account ID"
                value={account.id}
                copyKey="id"
                copiedKey={copiedKey}
                onCopy={copy}
              />
              <CopyableField
                label="Auth0 ID"
                value={account.auth0_id}
                copyKey="auth0_id"
                copiedKey={copiedKey}
                onCopy={copy}
              />
              <CopyableField
                label="HubSpot Contact ID"
                value={account.hubspot_contact_id}
                copyKey="hubspot_contact_id"
                copiedKey={copiedKey}
                onCopy={copy}
              />
              <SimpleField label="Language" value={formatLocale(account.locale)} />
            </Stack>
          </Box>

          <Divider />

          {/* Application: status + lifecycle dates. The dates expose the
              "started but not submitted" gap admins care about, without
              expanding the status enum. */}
          <Box>
            <Heading size="md" marginBottom={4}>
              Application
            </Heading>
            <Stack spacing={3}>
              <SimpleField
                label="Status"
                value={
                  <Badge colorScheme={statusInfo.colorScheme} fontSize="md">
                    {statusInfo.text}
                  </Badge>
                }
              />
              <SimpleField
                label="Started"
                value={
                  account.application_started_at
                    ? formatDate(account.application_started_at)
                    : "—"
                }
              />
              <SimpleField
                label="Submitted"
                value={
                  account.application_submitted_at
                    ? formatDate(account.application_submitted_at)
                    : "—"
                }
              />
              <SimpleField
                label="Last updated"
                value={
                  account.application_updated_at
                    ? formatDate(account.application_updated_at)
                    : "—"
                }
              />
              <SimpleField
                label="Last reviewed"
                value={account.last_review_at ? formatDate(account.last_review_at) : "—"}
              />
              <SimpleField
                label="Willing to travel"
                value={formatBoolean(account.personal_travel_willing)}
              />
              {/* Applicant-authored free text, so it gets a stacked block
                  rather than a label/value row — it can run long. */}
              {account.additional_info && (
                <Box paddingBlockStart={2}>
                  <Text fontWeight="medium" marginBottom={1}>
                    Anything else we should know
                  </Text>
                  <Text whiteSpace="pre-wrap">{account.additional_info}</Text>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Patient section — only meaningful for proxy applications.
              For self apps the patient IS the account holder and is shown
              in Personal above. */}
          {isProxy && (
            <>
              <Divider />
              <PersonaSection title="Patient (applied for by proxy)" patient={account.patient} />
            </>
          )}
        </VStack>
      </Grid>

      {hasApplication && (
        <>
          <Divider marginTop={8} />
          <Box marginTop={8}>
            <Heading size="md" marginBottom={2}>
              Danger zone
            </Heading>
            <Text color="gray.600" marginBottom={4} maxWidth="container.sm">
              Deleting the application resets this account to its post-signup state. Use it when an
              applicant needs to change an answer the flow locks after the first pass — country, or
              self vs. proxy — so they can go through it again.
            </Text>
            <DangerButton
              buttonText="Delete application"
              actionLabel="Delete application"
              modalHeader="Delete this application?"
              modalBody={
                <Stack spacing={3}>
                  <Text>
                    This permanently deletes the application for <b>{account.email}</b>, including:
                  </Text>
                  <UnorderedList>
                    <ListItem>
                      every uploaded medical record — eye exams, imaging, blood work and surgical
                      records
                    </ListItem>
                    <ListItem>every review decision and its snapshot</ListItem>
                    <ListItem>every consent answer</ListItem>
                  </UnorderedList>
                  <Text>
                    The account and its login are kept, so the applicant can sign back in and start
                    a new application. This cannot be undone.
                  </Text>
                </Stack>
              }
              onProceed={() => handleDeleteApplication(account.id, account.email)}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
